import type { Cabinet, User } from '@fidalia/api';
import posthog from 'posthog-js';

import { env } from '@/env';

/**
 * Mesure d'audience produit (PostHog, hébergé dans l'UE).
 *
 * Règles non négociables, parce que l'écran affiche des données de patients :
 *  - jamais de nom, d'e-mail, de téléphone ni de motif : seuls des identifiants
 *    internes et des états (formule, rôle, booléens) sont envoyés ;
 *  - enregistrement de session activé, mais tout le texte et tous les champs
 *    sont masqués à la source : le replay montre où l'on clique, jamais ce qui
 *    est écrit. Ni requêtes réseau, ni console enregistrées ;
 *  - pas d'autocapture des clics : elle embarquerait le texte d'un bouton, donc
 *    le nom d'un patient dans la file d'attente ;
 *  - les pages publiques à jeton (avis patient, retour OAuth) ne sont jamais
 *    suivies : leur adresse est un secret.
 *
 * Inactif hors production, ou si la clé est vide.
 */
const enabled = import.meta.env.PROD && env.VITE_POSTHOG_KEY !== '';

/** Fragments d'adresse qui portent un jeton : l'événement est abandonné. */
const SECRET_PATHS = ['/reviews/', '/connexion/google'];

type Properties = Record<string, string | number | boolean | null>;

let initialized = false;

export function initAnalytics(): void {
  if (!enabled || initialized) return;
  // Pages publiques à jeton (avis patient, retour OAuth) : aucun suivi du tout.
  if (SECRET_PATHS.some((secret) => location.pathname.includes(secret))) return;
  initialized = true;

  posthog.init(env.VITE_POSTHOG_KEY, {
    api_host: env.VITE_POSTHOG_HOST,
    ui_host: 'https://eu.posthog.com',
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    // Application monopage : les vues sont envoyées à la main, à chaque
    // changement de route (voir `trackPageView`).
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: false,
    // Replay : tout est masqué, de façon que l'on voie le parcours sans lire les données.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      blockSelector: 'img, video, canvas',
      recordCrossOriginIframes: false,
    },
    enable_recording_console_log: false,
    capture_performance: false,
    // Erreurs JavaScript non gérées : permet de savoir quel utilisateur a eu un problème.
    capture_exceptions: true,
    disable_surveys: true,
    respect_dnt: true,
    before_send: (event) => {
      if (!event) return null;
      const url = String(event.properties?.$current_url ?? '');
      return SECRET_PATHS.some((path) => url.includes(path)) ? null : event;
    },
  });
}

/** Une vue de page. Le chemin seul est envoyé, jamais la query ni le fragment. */
export function trackPageView(path: string): void {
  if (!enabled) return;
  if (SECRET_PATHS.some((secret) => path.includes(secret))) return;
  posthog.capture('$pageview', { $current_url: `${location.origin}${path}`, path });
}

export function track(event: string, properties?: Properties): void {
  if (!enabled) return;
  posthog.capture(event, properties);
}

/** Un événement émis une seule fois par navigateur (ex. extension détectée). */
export function trackOnce(event: string, properties?: Properties): void {
  if (!enabled) return;
  const key = `fidalia:analytics:${event}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    // Stockage indisponible (navigation privée) : on émet quand même.
  }
  posthog.capture(event, properties);
}

/**
 * Rattache la session à l'utilisateur et à son cabinet. Aucune donnée
 * personnelle : l'identifiant interne suffit à compter et à relier les actions.
 */
export function identifyUser(user: User, cabinet: Cabinet): void {
  if (!enabled) return;

  posthog.identify(user.id, {
    role: user.role,
    fonction: user.fonction,
    principal: user.principal,
    doctolib_connected: user.doctolibSyncAt !== null,
    onboarding_done: user.onboardingFait,
  });
  posthog.group('cabinet', cabinet.id, {
    plan: cabinet.formule,
    billing_period: cabinet.periode,
    status: cabinet.statut,
    practice_type: cabinet.exercice,
    team_size: cabinet.effectif,
    gold: cabinet.gold,
    created_at: cabinet.createdAt,
  });
}

/** À la déconnexion : l'appareil redevient anonyme. */
export function resetIdentity(): void {
  if (!enabled) return;
  posthog.reset();
}
